import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { UserContext } from "../../utils/contexts/UserContext";

const GAME_BASE_URL = process.env.REACT_APP_GAME_SERVER_URL;
const SOCKET_RECONNECTION_ATTEMPTS = 10;
const SOCKET_RECONNECTION_DELAY = 1000;
const SOCKET_CONNECTION_TIMEOUT = 40000;

// Global state for game results
let globalGameResult = null;
let globalShowModal = false;
let globalStakeAmount = 0;
let globalPlayerId = "";

export default function SelectedUser() {
  const {
    gameId,
    playerId,
    opponentId,
    opponentName,
    lobbyId,
    lobbyCode,
    gameName,
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userContext = React.useContext(UserContext);
  const socketRef = useRef(null);
  const connectionTimeoutRef = useRef(null);

  // Extract URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const avatarUrl = searchParams.get("avatar");
  const stakeAmount = searchParams.get("stakeAmount") || 0;

  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameResult, setGameResult] = useState(globalGameResult);
  const [showModal, setShowModal] = useState(globalShowModal);
  const [showRating, setShowRating] = useState(false);

  // Initialize global values on component mount
  useEffect(() => {
    globalPlayerId = playerId;
    globalStakeAmount = parseInt(stakeAmount) || 0;

    // Check for existing game result in localStorage
    const savedResult = localStorage.getItem("gameResult");
    if (savedResult) {
      try {
        const parsedResult = JSON.parse(savedResult);
        setGameResult(parsedResult);
        setShowModal(true);
        globalGameResult = parsedResult;
        globalShowModal = true;
      } catch (e) {
        localStorage.removeItem("gameResult");
      }
    }
  }, [playerId, stakeAmount]);

  // Dummy avatars for demonstration; replace with real user data as needed
  const userAvatars = {
    [playerId]: userContext?.user?.avatar,
    [opponentId]: avatarUrl,
  };

  // Dummy names for demonstration; replace with real user data as needed
  const userNames = {
    [playerId]: userContext?.user?.username,
    [opponentId]: opponentName,
  };

  const cleanupSocket = () => {
    if (socketRef.current) {

      // Clear heartbeat interval if it exists
      if (socketRef.current.heartbeatInterval) {
        clearInterval(socketRef.current.heartbeatInterval);
        socketRef.current.heartbeatInterval = null;
      }

      // Remove all event listeners
      socketRef.current.off("connect");
      socketRef.current.off("disconnect");
      socketRef.current.off("connect_error");
      socketRef.current.off("gameStarting");
      socketRef.current.off("gameStarted");

      // Notify server before disconnecting
      if (socketRef.current.connected) {
        socketRef.current.emit("user-leaving", {
          userId: userContext?.user?._id,
          gameName: gameName,
        });
      }

      // Disconnect socket
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsSocketConnected(false);
    }
  };

  const closeGameWindow = () => {
    try {
      if (window.gameWindow && !window.gameWindow.closed) {
        window.gameWindow.close();
      }
    } catch (err) {
    }
    window.gameWindow = null; // Always clear it
  };

  const initializeSocket = () => {
    return new Promise((resolve, reject) => {
      cleanupSocket();


      const token = sessionStorage.getItem("token");
      const parsedToken = JSON.parse(token);

      const socket = io(GAME_BASE_URL, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: SOCKET_RECONNECTION_ATTEMPTS,
        reconnectionDelay: SOCKET_RECONNECTION_DELAY,
        timeout: SOCKET_CONNECTION_TIMEOUT,
        query: {
          userId: userContext?.user?._id,
          token: `Bearer ${parsedToken.accessToken}`,
        },
      });

      connectionTimeoutRef.current = setTimeout(() => {
        reject(new Error("Socket connection timeout"));
        cleanupSocket();
      }, SOCKET_CONNECTION_TIMEOUT);

      socket.on("connect", () => {
        // console.log("Socket connected:", socket.id);
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setConnectionError(false);
        setIsSocketConnected(true);
        resolve(socket);
      });

      socket.on("disconnect", () => {
        // console.log("Socket disconnected");
        setIsSocketConnected(false);
        setConnectionError(true);
      });

      socket.on("connect_error", (error) => {
        // console.error("Socket connection error:", error);
        setIsSocketConnected(false);
        setConnectionError(true);
        reject(error);
      });

      socket.on("gameStarting", () => {
        // console.log("Game is starting...");
        setIsLoading(true);
      });

      socket.on("gameStarted", () => {
        // console.log("Game has started");
        setGameStarted(true);
        setIsLoading(false);
      });

      socket.on("gameEnd", (result) => {
        // console.log("Game ended with result:", result);
        // Try to close the game window if it exists
        // console.log("Attempting to close game window...");
        // console.log("Game window exists:", Boolean(window.gameWindow));

        if (window.gameWindow) {
          try {
            if (!window.gameWindow.closed) {
              window.gameWindow.close();
              window.gameWindow = null; // Clear the reference after closing
            }
          } catch (error) {
            console.error("Error closing game window:", error);
          }
        }
        // Create a complete result object
        const gameEndResult = {
          ...result,
          playerId: playerId, // Add playerId to determine winner/loser
          stakeAmount: parseInt(stakeAmount) || 0,
        };

        // Store in global state and localStorage
        globalGameResult = gameEndResult;
        globalShowModal = true;
        localStorage.setItem("gameResult", JSON.stringify(gameEndResult));

        // Update state
        setGameResult(gameEndResult);
        setShowModal(true);
        setGameStarted(false);

        // Notify server that player has received the result
        socket.emit("resultReceived", {
          gameId,
          playerId,
          result: gameEndResult,
        });

        closeGameWindow();

        // Make one final attempt to close the game window if it's still open
        if (window.gameWindow) {
          try {
            if (!window.gameWindow.closed) {
              window.gameWindow.close();
            }
            window.gameWindow = null; // Clear the reference regardless
          } catch (error) {
            console.error("Error closing game window:", error);
          }
        }

        // Add a small delay before navigating to ensure window has time to close
        setTimeout(() => {
          navigate("/");
        }, 300); // 300ms delay to give the window closing operation time to complete
      });

      socketRef.current = socket;
    });
  };

  const handleConnectionRetry = () => {
    setConnectionError(false);
    setIsLoading(true);

    initializeSocket()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Connection retry failed:", error);
        setConnectionError(true);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    initializeSocket()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to connect socket:", error);
        setConnectionError(true);
        setIsLoading(false);
      });

    return () => {
      cleanupSocket();
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }

      // Clean up game window reference on component unmount if it exists
      if (window.gameWindow && !window.gameWindow.closed) {
        try {
          window.gameWindow.close();
        } catch (error) {
          console.error("Error closing game window during cleanup:", error);
        }
        window.gameWindow = null;
      }
    };
  }, [userContext?.user?._id]);

  const handlePlayGame = () => {
    // Format game name properly (capitalize first letter)
    const formattedGameName = gameName
      ? gameName.charAt(0).toUpperCase() + gameName.slice(1)
      : "Chess";

    // gameId could be either a name like "snooker" or an ID like "6668d6a7cd456579043fa928"
    // If it's a name and not an ID, we need to fetch the actual ID from somewhere
    // For now, we'll use the gameId directly, as the server should handle both formats

    // Log a warning if gameId looks like a name instead of a MongoDB ID
    if (gameId && !gameId.match(/^[0-9a-f]{24}$/)) {
      console.warn(
        "Warning: gameId appears to be a name rather than a MongoDB ID:",
        gameId
      );
      console.warn(
        "The game server expects a MongoDB ID like '6668d6a7cd456579043fa928'"
      );
    }

    // Create full game data object matching mobile implementation
    const gameData = {
      gameId: gameId,
      playerId: playerId,
      opponentId: opponentId,
      gameName: formattedGameName,
      lobbyId: lobbyId,
      lobbyCode: lobbyCode,
      stakeAmount: parseInt(stakeAmount) || 0,
    };

    // Create the game URL with all required parameters
    const url = `${GAME_BASE_URL}/game?gameId=${gameId}&playerId=${playerId}&opponentId=${opponentId}&gameName=${formattedGameName}&lobbyId=${lobbyId}&lobbyCode=${lobbyCode}&stakeAmount=${stakeAmount}`;


    // If socket is connected, notify server
    if (socketRef.current?.connected) {
      // Notify server game is starting with complete game data
      socketRef.current.emit("gameStarting", gameData);
    } else {
      console.warn("Socket not connected, proceeding without notification");
    }

    // Store game data in localStorage for potential recovery
    localStorage.setItem(
      "lastGameData",
      JSON.stringify({
        ...gameData,
        timestamp: Date.now(),
      })
    );

    // Open game in new window regardless of socket connection
    const gameWindow = window.open(url, "_blank");

    // Store the reference globally so it can be closed when game ends
    window.gameWindow = gameWindow;

    // Listen for window close event to handle user manual closing
    if (gameWindow) {
      const checkWindowClosed = setInterval(() => {
        if (gameWindow.closed) {
          clearInterval(checkWindowClosed);
          // Check if the game ended properly with a result already
          // If not, we can handle it as a forfeit or just return to home
          if (!localStorage.getItem("gameResult")) {
            navigate("/");
          }
        }
      }, 1000);
    }
  };

  if (connectionError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#130828",
          color: "#FFF",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 16px",
        }}
      >
        <div
          style={{
            color: "#FFF",
            fontSize: 18,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          Lost connection to the game server
        </div>
        <button
          onClick={handleConnectionRetry}
          style={{
            backgroundColor: "#FFCC00",
            padding: "15px",
            borderRadius: 8,
            width: "80%",
            textAlign: "center",
            marginBottom: 10,
            border: "none",
            color: "#000000",
            fontSize: 16,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Retry Connection
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: "transparent",
            border: "2px solid #FFCC00",
            padding: "15px",
            borderRadius: 8,
            width: "80%",
            textAlign: "center",
            color: "#FFCC00",
            fontSize: 16,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Exit Game
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#130828] text-white flex flex-col items-center px-4 py-8">
      {/* Header */}
      <div className="w-full flex items-center mb-8">
        <button
          onClick={() => navigate(-1)}
          className="bg-none border-none text-white text-2xl mr-2"
          aria-label="Go back"
        >
          &larr;
        </button>
        <span className="flex-1 text-center font-extrabold tracking-widest text-[#B0FF4A] text-lg">
          Selected <span className="text-[#FF43B9]">User</span>
        </span>
      </div>

      {/* User vs Opponent */}
      <div className="w-full flex flex-col items-center gap-8">
        {/* YOU */}
        <div className="flex flex-col items-center gap-2">
          <div className="relative mb-2">
            <img
              src={userAvatars[playerId]}
              alt="You"
              className="w-24 h-24 rounded-full border-[6px] border-pink-300 bg-white"
            />
            <span className="absolute left-1/2 -top-4 -translate-x-1/2 bg-[#1CB0F6] text-white font-bold text-xs rounded-xl px-2 py-[2px]">
              YOU
            </span>
          </div>
          <div className="font-bold text-lg text-center capitalize">
            {userNames[playerId]}
          </div>
        </div>

        {/* VS */}
        <div className="text-[#B0FF4A] text-4xl font-extrabold my-2">VS</div>

        {/* OPPONENT */}
        <div className="flex flex-col items-center gap-2">
          <div className="relative mb-2">
            <img
              src={userAvatars[opponentId]}
              alt="Opponent"
              className="w-24 h-24 rounded-full border-[6px] border-[#B0FF4A] bg-white"
            />
            <span className="absolute right-1/2 -top-4 translate-x-1/2 bg-[#1CB0F6] text-white font-bold text-xs rounded-xl px-2 py-[2px]">
              OPPONENT
            </span>
          </div>
          <div className="font-bold text-lg text-center capitalize">
            {userNames[opponentId]}
          </div>
        </div>
      </div>

      {/* Play Game Button */}
      <button
        onClick={handlePlayGame}
        disabled={isLoading || !isSocketConnected}
        className={`
      w-full bg-yellow-400 text-[#130828] font-extrabold text-lg rounded-md py-4 mt-10 mb-4
      disabled:opacity-70 disabled:cursor-not-allowed
      transition-opacity duration-200
    `}
      >
        {isLoading ? "Starting Game..." : "PLAY GAME"}
      </button>

      {/* Disclaimer Text */}
      <div className="text-white text-opacity-70 text-sm text-center mt-2 px-2">
        Once you start the game, if you exit from the game, you will lose your
        stake
      </div>
    </div>
  );
}
